/**
 * IndiaHolidayDecor — tricolor decoration for Indian national holidays.
 *
 * Active windows (IST dates):
 *   Republic Day    : Jan 24 – Jan 28  (2 days before Jan 26, 2 days after)
 *   Independence Day: Aug 13 – Aug 17  (2 days before Aug 15, 2 days after)
 *
 * Renders a fixed tricolor stripe + hanging bunting at the top of the viewport.
 * Purely additive — no existing layout element is touched.
 */

import React, { useMemo } from "react";

// --- date helpers -----------------------------------------------------------

/** Return {month, day} in Asia/Kolkata timezone */
function todayIST() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    month: "numeric",
    day: "numeric",
  }).formatToParts(now);
  const get = (t) => parseInt(parts.find((p) => p.type === t)?.value ?? "0", 10);
  return { month: get("month"), day: get("day") };
}

/**
 * Returns the active holiday info, or null when outside all windows.
 *   window = [holidayMonth, holidayDay, before, after] (days before/after)
 */
const HOLIDAYS = [
  { month: 1,  day: 26, name: "Republic Day",     emoji: "🇮🇳" },
  { month: 8,  day: 15, name: "Independence Day",  emoji: "🇮🇳" },
];
const BEFORE = 2;
const AFTER  = 2;

function activeHoliday() {
  const { month, day } = todayIST();
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const year = now.getFullYear();

  for (const h of HOLIDAYS) {
    const hDate  = new Date(year, h.month - 1, h.day);
    const start  = new Date(year, h.month - 1, h.day - BEFORE);
    const end    = new Date(year, h.month - 1, h.day + AFTER);
    const today  = new Date(year, month - 1, day);
    if (today >= start && today <= end) return h;
  }
  return null;
}

// --- SVG bunting flag -------------------------------------------------------

function BuntingFlag({ color, style }) {
  return (
    <svg
      width="22"
      height="28"
      viewBox="0 0 22 28"
      style={style}
      aria-hidden="true"
    >
      <polygon points="0,0 22,0 11,28" fill={color} />
    </svg>
  );
}

// --- main component ---------------------------------------------------------

const SAFFRON = "#FF9933";
const WHITE   = "#FFFFFF";
const GREEN   = "#138808";
const NAVY    = "#000080";

const BUNTING_COLORS = [SAFFRON, WHITE, GREEN, SAFFRON, WHITE, GREEN, SAFFRON, WHITE, GREEN, SAFFRON, WHITE, GREEN, SAFFRON, WHITE, GREEN];

export default function IndiaHolidayDecor() {
  const holiday = useMemo(() => activeHoliday(), []);
  if (!holiday) return null;

  return (
    <>
      <style>{`
        @keyframes flg-swing {
          0%,100% { transform: rotate(-4deg); }
          50%      { transform: rotate(4deg); }
        }
        @keyframes flg-fadein {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .flg-hd-bar {
          position: fixed;
          top: 0; left: 0; right: 0;
          height: 6px;
          display: flex;
          z-index: 10000;
          pointer-events: none;
        }
        .flg-hd-bar-s { flex: 1; background: ${SAFFRON}; }
        .flg-hd-bar-w { flex: 1; background: ${WHITE}; }
        .flg-hd-bar-g { flex: 1; background: ${GREEN}; }

        .flg-hd-bunting {
          position: fixed;
          top: 6px; left: 0; right: 0;
          height: 36px;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          z-index: 9999;
          pointer-events: none;
          overflow: hidden;
        }
        .flg-hd-bunting-flag {
          transform-origin: top center;
          animation: flg-swing 3s ease-in-out infinite;
          margin: 0 1px;
          flex-shrink: 0;
        }

        .flg-hd-label {
          align-self: center;
          margin: 0 10px;
          flex-shrink: 0;
          background: rgba(255,255,255,0.95);
          border: 1.5px solid ${SAFFRON};
          border-radius: 999px;
          padding: 2px 14px 2px 10px;
          font-size: 11.5px;
          font-weight: 600;
          color: ${NAVY};
          white-space: nowrap;
          pointer-events: none;
          animation: flg-fadein 0.6s ease both;
          box-shadow: 0 1px 6px rgba(0,0,0,0.10);
          letter-spacing: 0.01em;
          display: flex;
          align-items: center;
          gap: 5px;
          /* pull up slightly so it sits centred against the flag tips */
          margin-top: -2px;
        }
        .flg-hd-label-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          background: ${SAFFRON};
          flex-shrink: 0;
        }

        /* push content down: 6px stripe + 36px bunting */
        body.flg-india-holiday-active {
          padding-top: 42px !important;
        }
      `}</style>

      {/* inject body class so the layout shifts down */}
      <BodyClassEffect className="flg-india-holiday-active" />

      {/* tricolor stripe */}
      <div className="flg-hd-bar" aria-hidden="true">
        <div className="flg-hd-bar-s" />
        <div className="flg-hd-bar-w" />
        <div className="flg-hd-bar-g" />
      </div>

      {/* bunting flags with greeting pill centred inline */}
      <div className="flg-hd-bunting">
        {BUNTING_COLORS.slice(0, 7).map((color, i) => (
          <BuntingFlag
            key={`l${i}`}
            color={color}
            style={{ animationDelay: `${i * 0.18}s` }}
          />
        ))}

        <div className="flg-hd-label" role="status" aria-live="polite">
          <span className="flg-hd-label-dot" />
          Happy {holiday.name} {holiday.emoji}
        </div>

        {BUNTING_COLORS.slice(0, 7).map((color, i) => (
          <BuntingFlag
            key={`r${i}`}
            color={color}
            style={{ animationDelay: `${(i + 7) * 0.18}s` }}
          />
        ))}
      </div>
    </>
  );
}

/** Adds/removes a CSS class on <body> for the lifetime of this component. */
function BodyClassEffect({ className }) {
  React.useEffect(() => {
    document.body.classList.add(className);
    return () => document.body.classList.remove(className);
  }, [className]);
  return null;
}
